import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Flame, Image as ImageIcon, Sparkles, ChevronRight, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

import { getTimeAgo } from '../lib/dateUtils';

// 9to5Google Style Article Card with Signature Floating Green Card Accent Frame
const NineToFiveCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title || 'Untitled');
  const articleUrl = `/news/${generateSlug(title)}`;
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCommentsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onArticleClick) {
      onArticleClick(article);
      setTimeout(() => {
        const commentsEl = document.getElementById('comments-section') || document.querySelector('.comments-container');
        if (commentsEl) commentsEl.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    }
  };

  const categoryTag = (article.category || article.topic || article.source || 'YEGO NEWS').toUpperCase();
  const authorName = article.author || article.source || 'YEGO Staff';
  const authorAvatar = article.author_image || article.author_photo || article.user_avatar || null;
  const timeAgo = getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda);
  const [commentsCount, setCommentsCount] = useState(
    typeof article.comments_count === 'number'
      ? article.comments_count
      : (Array.isArray(article.comments) ? article.comments.length : 0)
  );

  useEffect(() => {
    if (typeof article.comments_count === 'number') {
      setCommentsCount(article.comments_count);
    }
    const artId = article.id || article._id;
    if (artId) {
      axios.get(`${BACKEND_URL}/api/comments/${artId}`)
        .then(res => {
          if (Array.isArray(res.data)) {
            setCommentsCount(res.data.length);
          }
        })
        .catch(() => {});
    }
  }, [article.id, article._id, article.comments_count]);

  useEffect(() => {
    const handleCommentChange = (e) => {
      const artId = String(article.id || article._id || '');
      if (e.detail?.articleId && String(e.detail.articleId) === artId) {
        if (typeof e.detail.count === 'number') {
          setCommentsCount(e.detail.count);
        }
      }
    };
    window.addEventListener('yego_comment_changed', handleCommentChange);
    return () => window.removeEventListener('yego_comment_changed', handleCommentChange);
  }, [article.id, article._id]);

  // Extract clean text excerpt (stripped of HTML tags)
  const getExcerpt = () => {
    const raw = (isKinyarwanda && article.content_rw) 
      ? article.content_rw 
      : (article.summary || article.description || article.content || '');
    if (!raw) return '';
    const clean = raw.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim();
    return clean.length > 210 ? clean.substring(0, 210) + '...' : clean;
  };

  const excerpt = getExcerpt();

  return (
    <article className="group cursor-pointer pb-7 mb-7 border-b border-gray-200/70 dark:border-gray-800/80 last:border-b-0 px-1 sm:px-2">
      <Link to={articleUrl} onClick={handleClick} className="block">
        {/* 1. Category / Topic Tag */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            {categoryTag}
          </span>
        </div>

        {/* 2. Main Headline / Title */}
        <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white leading-[1.25] tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors mb-2.5">
          {title}
        </h2>

        {/* 3. Byline Meta: Author Avatar + Name + Pipe + Date */}
        <div className="flex items-center gap-2 text-xs mb-1.5 flex-wrap">
          <div className="w-5 h-5 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-[10px] flex-shrink-0 shadow-xs">
            {authorAvatar ? (
              <img src={authorAvatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span>{(authorName || 'Y').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <span className="font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
            {authorName}
          </span>
          <span className="text-gray-300 dark:text-gray-700 font-light">|</span>
          <span className="text-gray-500 dark:text-gray-400 font-normal">
            {timeAgo}
          </span>
        </div>

        {/* 4. Comments Count Row */}
        <div 
          onClick={handleCommentsClick}
          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-3 hover:underline w-fit"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span>{commentsCount} {isKinyarwanda ? 'Ibitekerezo' : 'Comments'}</span>
        </div>

        {/* 5. The Signature 9to5Google Floating Image Card with Green Accent Frame */}
        <div className="relative my-4 mx-2.5 sm:mx-3 group/img">
          {/* Top-Left Green Frame Accent */}
          <div 
            className="absolute -top-2.5 -left-2.5 w-1/4 h-[calc(100%+10px)] border-t-[2.5px] border-l-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
            aria-hidden="true" 
          />
          {/* Bottom-Right Green Frame Accent */}
          <div 
            className="absolute -bottom-2.5 -right-2.5 w-[calc(100%+10px)] h-1/4 border-b-[2.5px] border-r-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
            aria-hidden="true" 
          />

          {/* Foreground Image Card */}
          <div className="relative z-10 w-full aspect-[16/10] sm:aspect-[16/10] bg-white dark:bg-gray-900 overflow-hidden shadow-sm border border-gray-200/60 dark:border-gray-800">
            {article.image_url ? (
              <img
                src={article.image_url}
                alt={title}
                className="w-full h-full object-cover object-top group-hover/img:scale-[1.015] transition-transform duration-300"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <ImageIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              </div>
            )}
          </div>
        </div>

        {/* 6. Excerpt / Article Snippet */}
        {excerpt && (
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-3 mb-1 font-normal line-clamp-3 sm:line-clamp-4">
            {excerpt}
          </p>
        )}
      </Link>
    </article>
  );
};

// Hero Card - EXACT same style as existing FeaturedCard
const HeroCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link to={articleUrl} onClick={handleClick} className="block group cursor-pointer">
      <div className="flex flex-col h-full">
        {/* Image Container - No rounded corners, same as original */}
        <div className="relative aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt={title}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-12 w-12 text-gray-400" />
            </div>
          )}
        </div>
        
        {/* Content - Same typography as original */}
        <div className="pt-2 flex-1 flex flex-col justify-between">
          <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug group-hover:text-[#047857] dark:group-hover:text-emerald-400 transition-colors">
            {title}
          </h2>
          <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-1.5">
            <span className="text-[#047857] dark:text-emerald-400 font-medium">{article.source}</span>
            <span>•</span>
            <span>{getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Small Card - Text LEFT, Thumbnail RIGHT (80-100px), 3-line headline
const SmallCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link 
      to={articleUrl} 
      onClick={handleClick} 
      className="flex gap-3 group py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0 cursor-pointer"
    >
      {/* Text LEFT */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <h3 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-[#047857] dark:group-hover:text-emerald-400 transition-colors">
          {title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-gray-500">
          <span>{article.source}</span>
          <span>•</span>
          <span>{getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}</span>
        </div>
      </div>
      
      {/* Thumbnail RIGHT - Fixed 80-100px square */}
      {article.image_url && (
        <div className="flex-shrink-0 w-20 h-20 overflow-hidden bg-gray-200 dark:bg-gray-800">
          <img
            src={article.image_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
    </Link>
  );
};

// Trending Card for 2x3 Grid - Same style as original, NO number badge
const TrendingCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  const handleClick = (e) => {
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
      return;
    }
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
    }
  };

  return (
    <Link to={articleUrl} onClick={handleClick} className="block group cursor-pointer">
      <div className="flex flex-col">
        {/* Image - Same as original */}
        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-gray-400" />
            </div>
          )}
        </div>
        {/* Content - No line-clamp, headline expands */}
        <div className="pt-2">
          <h3 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
            {title}
          </h3>
          <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-500">
            <Clock className="h-3 w-3" />
            {getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}
          </div>
        </div>
      </div>
    </Link>
  );
};

// Ad Slot Component
const AdSlot = ({ ad }) => {
  useEffect(() => {
    if (ad?.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/impression`).catch(() => {});
    }
  }, [ad]);

  if (!ad) {
    return (
      <div className="my-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-y border-gray-200 dark:border-gray-700">
        <div className="text-center text-[10px] text-gray-400 uppercase tracking-wide">
          Advertisement
        </div>
      </div>
    );
  }

  const handleClick = () => {
    if (ad?.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/click`).catch(() => {});
    }
  };

  return (
    <div className="my-4">
      <a 
        href={ad.link} 
        target="_blank" 
        rel="noopener noreferrer"
        onClick={handleClick}
        className="block"
      >
        {ad.image_url && (
          <div className="relative">
            <img 
              src={ad.image_url} 
              alt={ad.title || 'Advertisement'} 
              className="w-full h-auto object-cover"
            />
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 text-white text-[10px] rounded uppercase">
              Ad
            </span>
          </div>
        )}
      </a>
    </div>
  );
};

// For You Card - for horizontal scroll grid
const ForYouCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link 
      to={articleUrl} 
      onClick={handleClick}
      className="block group cursor-pointer"
    >
      <div className="flex flex-col">
        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
              <Sparkles className="h-8 w-8 text-gray-300" />
            </div>
          )}
        </div>
        <div className="pt-2">
          <h4 className="text-xs font-medium text-gray-800 dark:text-gray-100 leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
            {title}
          </h4>
          <p className="text-[9px] text-gray-400 mt-1">
            {article.source || 'YegoNews'}
          </p>
        </div>
      </div>
    </Link>
  );
};

// Mobile Trending Section - 2x3 Grid with Ad slots above and below
const MobileTrendingSection = ({ articles, isKinyarwanda, getAdForPosition, onArticleClick }) => {
  if (!articles || articles.length === 0) return null;

  return (
    <div className="my-4">
      {/* Ad Slot Above Trending */}
      <AdSlot ad={getAdForPosition('above-trending')} />
      
      <section className="my-4">
        {/* Header - Same style as original */}
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-[#D90429] rounded-lg">
            <Flame className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wide">
            {isKinyarwanda ? 'Inkuru Zishyushye' : 'Trending Now'}
          </h2>
        </div>

        {/* 2x3 Grid */}
        <div className="grid grid-cols-2 gap-3">
          {articles.slice(0, 6).map((article, index) => (
            <TrendingCard 
              key={article.id || index} 
              article={article} 
              isKinyarwanda={isKinyarwanda}
              onArticleClick={onArticleClick}
            />
          ))}
        </div>
      </section>
      
      {/* Ad Slot Below Trending */}
      <AdSlot ad={getAdForPosition('below-trending')} />
    </div>
  );
};

// Mobile For You Section - 2x3 horizontal scrolling grid (like Sponsored)
const MobileForYouSection = ({ articles, isKinyarwanda, onArticleClick }) => {
  if (!articles || articles.length === 0) return null;

  // Group articles into chunks of 6 for 2x3 grid pages
  const chunkedArticles = [];
  for (let i = 0; i < articles.length; i += 6) {
    chunkedArticles.push(articles.slice(i, i + 6));
  }

  return (
    <section className="my-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-gray-900 dark:border-gray-100">
        <h3 className="text-[10px] font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-purple-600" />
          {isKinyarwanda ? 'Kuri Wowe' : 'For You'}
        </h3>
        <Link to="/for-you" className="text-[10px] text-gray-500 hover:text-emerald-600 flex items-center gap-1 uppercase tracking-wide">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      
      {/* Horizontal scroll of 2x3 grid pages */}
      <div className="overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        <div className="flex gap-3" style={{ width: 'max-content' }}>
          {chunkedArticles.slice(0, 4).map((chunk, chunkIndex) => (
            <div key={chunkIndex} className="w-[calc(100vw-32px)] flex-shrink-0 snap-start">
              <div className="grid grid-cols-2 gap-3">
                {chunk.map((article, index) => (
                  <ForYouCard 
                    key={article.id || `foryou-${chunkIndex}-${index}`} 
                    article={article} 
                    isKinyarwanda={isKinyarwanda} 
                    onArticleClick={onArticleClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Mobile Feed Component
const MobileFeed = ({ 
  articles = [], 
  trendingArticles = [],
  forYouArticles = [],
  adsData = {},
  forYouSection,
  yegoTvSection,
  sponsoredSection,
  announcementsSection,
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  // Get ad for a position
  const getAdForPosition = (position) => {
    if (!adsData.ads_enabled || !adsData.positions) return null;
    const positionAds = adsData.positions[position] || [];
    return positionAds.length > 0 ? positionAds[0] : null;
  };

  // Build the automatic layout pattern in 9to5Google style
  const buildFeedLayout = () => {
    const layout = [];
    let articleIndex = 0;

    // Helper to get next N articles
    const getNextArticles = (count) => {
      const result = articles.slice(articleIndex, articleIndex + count);
      articleIndex += count;
      return result;
    };

    // 1. First 4 Articles in 9to5Google style
    const batch1 = getNextArticles(4);
    if (batch1.length > 0) {
      layout.push({ type: '9to5-batch', articles: batch1, key: '9to5-1' });
    }

    // 2. Ad Slot 1
    layout.push({ type: 'ad', position: 'in-feed-1', key: 'ad-1' });

    // 3. Next 4 Articles in 9to5Google style
    const batch2 = getNextArticles(4);
    if (batch2.length > 0) {
      layout.push({ type: '9to5-batch', articles: batch2, key: '9to5-2' });
    }

    // 4. Trending Now (2x3 grid)
    layout.push({ type: 'trending', key: 'trending' });

    // 5. Next 4 Articles in 9to5Google style
    const batch3 = getNextArticles(4);
    if (batch3.length > 0) {
      layout.push({ type: '9to5-batch', articles: batch3, key: '9to5-3' });
    }

    // 6. Ad Slot 2
    layout.push({ type: 'ad', position: 'in-feed-2', key: 'ad-2' });

    // 7. Remaining articles in 9to5Google style
    const batch4 = getNextArticles(50);
    if (batch4.length > 0) {
      layout.push({ type: '9to5-batch', articles: batch4, key: '9to5-4' });
    }

    // 8. Existing sections (For You, YegoTV, Sponsored, YEGO Ads & Partners)
    layout.push({ type: 'existing-sections', key: 'existing' });

    // 9. Ad Slot 3
    layout.push({ type: 'ad', position: 'in-feed-3', key: 'ad-3' });

    return layout;
  };

  const feedLayout = buildFeedLayout();

  return (
    <div className="md:hidden">
      {feedLayout.map((item) => {
        switch (item.type) {
          case '9to5-batch':
          case 'hero-batch':
            return (
              <div key={item.key} className="space-y-1 mb-4">
                {item.articles.map((article, idx) => (
                  <NineToFiveCard 
                    key={article.id || `${item.key}-${idx}`} 
                    article={article} 
                    isKinyarwanda={isKinyarwanda} 
                    onArticleClick={onArticleClick}
                  />
                ))}
              </div>
            );

          case 'hero-single':
            return (
              <div key={item.key} className="mb-4">
                {item.articles.map((article, idx) => (
                  <NineToFiveCard 
                    key={article.id || `${item.key}-${idx}`} 
                    article={article} 
                    isKinyarwanda={isKinyarwanda} 
                    onArticleClick={onArticleClick}
                  />
                ))}
              </div>
            );

          case 'small-batch':
            return (
              <div key={item.key} className="mb-4">
                {item.articles.map((article, idx) => (
                  <SmallCard 
                    key={article.id || `${item.key}-${idx}`} 
                    article={article} 
                    isKinyarwanda={isKinyarwanda} 
                    onArticleClick={onArticleClick}
                  />
                ))}
              </div>
            );

          case 'trending':
            return (
              <MobileTrendingSection 
                key={item.key}
                articles={trendingArticles}
                isKinyarwanda={isKinyarwanda}
                getAdForPosition={getAdForPosition}
                onArticleClick={onArticleClick}
              />
            );

          case 'ad':
            return (
              <AdSlot 
                key={item.key}
                ad={getAdForPosition(item.position)}
              />
            );

          case 'existing-sections':
            return (
              <div key={item.key} className="space-y-6">
                {yegoTvSection}
                <AdSlot ad={getAdForPosition('between-yegotv-foryou')} />
                <MobileForYouSection 
                  articles={forYouArticles}
                  isKinyarwanda={isKinyarwanda}
                  onArticleClick={onArticleClick}
                />
                {sponsoredSection}
                {announcementsSection}
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default MobileFeed;
